import {ProductVariant} from '@shopify/hydrogen/storefront-api-types';
import React, {useCallback} from 'react';
import clsx from 'clsx';
import {Image} from '@shopify/hydrogen';
import {DATA_TEST_IDS} from '~/lib/const';

type EventType = React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>;
function VariantButton({
  isActive,
  variant,
  onHoverAction,
  onSelect,
}: {
  isActive: boolean;
  variant: ProductVariant;
  onSelect: (id: string) => void;
  onHoverAction: (event: EventType, id: string, isHovered: boolean) => void;
}) {
  const styles = clsx(
    'h-[44px] w-[44px] aspect-square ml-2.5 mt-3',
    !isActive && 'bg-gray-200 border border-gray-50',
    isActive && 'border-solid border-[1px] border-black',
  );

  const handleMouseOver = useCallback(
    (event: EventType) => {
      onHoverAction(event, variant.id, true);
    },
    [onHoverAction, variant.id],
  );

  const handleMouseOut = useCallback(
    (event: EventType) => {
      onHoverAction(event, variant.id, false);
    },
    [onHoverAction, variant.id],
  );

  const handleClick = useCallback(() => {
    onSelect(variant.id);
  }, [onSelect, variant.id]);

  return (
    <button
      data-testid={DATA_TEST_IDS.BUTTON('variant-selector')}
      key={variant.id}
      type={'button'}
      onClick={handleClick}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onFocus={handleMouseOver}
      onBlur={handleMouseOut}
    >
      {variant.image?.url && (
        <Image
          loading="lazy"
          key={variant.id}
          data={variant.image}
          alt={variant.image.altText ?? 'variant image'}
          className={styles}
          width={44}
          height={44}
          widths={[44]}
          sizes="44px"
        />
      )}
    </button>
  );
}

export function VariantSelector({
  activeVariant,
  variantsColorObject,
  onSelect,
  onHoverAction,
  availableSkus,
}: {
  activeVariant?: ProductVariant;
  variantsColorObject: {
    [key: string]: ProductVariant;
  };
  onSelect: (id: string | null) => void;
  onHoverAction: (
    event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
    id: string,
    isHovered: boolean,
  ) => void;
  availableSkus: any[];
}) {
  return (
    <div className="flex mt-2.5" data-testid={DATA_TEST_IDS.VARIANT_SELECTOR}>
      <div className={'flex flex-wrap -ml-2.5 min-h-[44px]'}>
        {Object.values(variantsColorObject).map((variant) => {
          const isActive =
            activeVariant != null && activeVariant.id === variant.id;

          return (
            availableSkus.indexOf(variant.sku) >= 0 && (
              <VariantButton
                key={variant.id}
                isActive={isActive}
                variant={variant}
                onHoverAction={onHoverAction}
                onSelect={onSelect}
              />
            )
          );
        })}
      </div>
    </div>
  );
}
